import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Tag as TagIcon } from 'lucide-react';
import config from '../../config';
import authService from '../../services/authService';

// Palette slots mirror TAG_COLORS in api/src/handlers/customerTags.ts. Written
// out as whole class strings rather than composed at runtime — Tailwind scans
// source statically, so `bg-${color}-900/50` would be purged from the build.
export const TAG_STYLES = {
  slate: 'bg-slate-700/60 text-slate-200 border-slate-600',
  sky: 'bg-sky-900/50 text-sky-200 border-sky-700',
  emerald: 'bg-emerald-900/50 text-emerald-200 border-emerald-700',
  amber: 'bg-amber-900/50 text-amber-200 border-amber-700',
  rose: 'bg-rose-900/50 text-rose-200 border-rose-700',
  violet: 'bg-violet-900/50 text-violet-200 border-violet-700',
  teal: 'bg-teal-900/50 text-teal-200 border-teal-700',
  orange: 'bg-orange-900/50 text-orange-200 border-orange-700'
};

export const TAG_COLOR_NAMES = Object.keys(TAG_STYLES);

export function tagClasses(color) {
  return TAG_STYLES[color] || TAG_STYLES.slate;
}

/** Read-only chip, as rendered in a customer row. */
export function TagChip({ tag, className = '' }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded text-xs border ${tagClasses(
        tag.color
      )} ${className}`}
    >
      {tag.name}
    </span>
  );
}

/**
 * Filter chip with three states: off -> include -> exclude -> off.
 *
 * Exclude exists so "everything except internal" is one mechanism rather than a
 * separate bespoke toggle per tag someone wants to hide.
 */
export function FilterChip({ label, count, state, onClick }) {
  const base =
    'inline-flex items-center whitespace-nowrap gap-1.5 px-2.5 py-1 rounded text-xs border transition select-none';
  const styles = {
    off: 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600',
    include: 'bg-indigo-900/50 text-indigo-200 border-indigo-600',
    exclude: 'bg-slate-900 text-slate-500 border-slate-700 line-through'
  };

  return (
    <button type="button" onClick={onClick} className={`${base} ${styles[state]}`}>
      {state === 'exclude' && <X size={11} className="shrink-0" />}
      <span>{label}</span>
      {typeof count === 'number' && (
        <span className="text-[0.65rem] opacity-60 tabular-nums">{count}</span>
      )}
    </button>
  );
}

/**
 * Assign tags to one customer, and create new ones inline.
 *
 * Sends the customer's FULL tag set (PUT semantics) rather than a delta, so the
 * dialog never has to reason about what changed.
 */
export function CustomerTagDialog({ customer, allTags, vendorTeamId, onClose, onSaved }) {
  const [selected, setSelected] = useState(() => new Set((customer.tags || []).map((t) => t.id)));
  const [newTagName, setNewTagName] = useState('');
  const [tags, setTags] = useState(allTags);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => setTags(allTags), [allTags]);

  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => a.name.localeCompare(b.name)),
    [tags]
  );

  const toggle = (tagId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  const handleCreate = async () => {
    const name = newTagName.trim();
    if (!name) return;
    setCreating(true);
    setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/customer-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
        body: JSON.stringify({ vendorTeamId, name })
      });
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body?.error || `Could not create tag (HTTP ${res.status})`);
      }
      setTags((prev) => [...prev, body.tag]);
      // A tag created from this dialog is one the user obviously wants applied.
      setSelected((prev) => new Set(prev).add(body.tag.id));
      setNewTagName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/api/customers/${encodeURIComponent(
          customer.customerProxyTeamId
        )}/tags`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authService.getAuthHeaders() },
          body: JSON.stringify({ tagIds: [...selected] })
        }
      );
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body?.error || `Could not save tags (HTTP ${res.status})`);
      }
      onSaved(
        customer.customerProxyTeamId,
        sortedTags.filter((t) => selected.has(t.id))
      );
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Tags for ${customer.customerName}`}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-700">
          <div>
            <h3 className="text-slate-100 font-semibold">Tags</h3>
            <p className="text-xs text-slate-400 mt-0.5">{customer.customerName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4">
          {sortedTags.length === 0 ? (
            <p className="text-sm text-slate-400 mb-4">
              No tags yet. Create the first one below — “internal”, “law-enforcement” and
              “trial” are good places to start.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 mb-4">
              {sortedTags.map((tag) => {
                const on = selected.has(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggle(tag.id)}
                    className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded text-xs border transition ${
                      on
                        ? tagClasses(tag.color)
                        : 'bg-slate-900 text-slate-500 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    {tag.name}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              value={newTagName}
              maxLength={40}
              placeholder="New tag name"
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreate();
                }
              }}
              className="flex-1 bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-2 py-1.5 focus:outline-none focus:border-slate-500"
            />
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newTagName.trim() || creating}
              className="inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm px-3 py-1.5 rounded transition disabled:opacity-50"
            >
              <Plus size={14} />
              Add
            </button>
          </div>

          {error && <p className="text-sm text-red-300 mt-3">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm px-3 py-1.5 rounded transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm px-3 py-1.5 rounded transition disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save tags'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { TagIcon };
